import React, { useState, createContext, useContext } from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Separator } from "./ui/separator";
import {
  Menu,
  LayoutDashboard,
  FolderHeart,
  Tags,
  Users,
  FileText,
  Settings,
  LogOut,
  Shield,
  Heart,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
} from "lucide-react";

const LOGO_URL = "./knots-logo.png";

// Sidebar context for collapse state
const SidebarContext = createContext({
  collapsed: false,
  setCollapsed: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

// Desktop Sidebar Component
const DesktopSidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    logout,
    isLeader,
    hasProjectAccess,
    hasInvitationAccess,
    getClientSingleProjectId,
    getClientSingleInvitationId,
  } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Build menu items based on access
  const menuItems = [];

  // Dashboard only for leaders
  if (isLeader()) {
    menuItems.push({
      path: "/dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
    });
  }

  // Projects if has access — clients go directly to their project
  if (hasProjectAccess()) {
    const clientProjectId = getClientSingleProjectId();
    menuItems.push({
      path: clientProjectId ? `/projects/${clientProjectId}` : "/projects",
      label: "Projects",
      icon: FolderHeart,
      activePrefix: "/projects",
    });
  }

  // Invitations if has access — clients go directly to their invitation
  if (hasInvitationAccess()) {
    const clientInvitationId = getClientSingleInvitationId();
    menuItems.push({
      path: clientInvitationId
        ? `/invitations/${clientInvitationId}`
        : "/invitations",
      label: "Invitations",
      icon: Heart,
      activePrefix: "/invitations",
    });
  }

  const leaderMenuItems = [
    { path: "/categories", label: "Categories", icon: Tags },
    { path: "/users", label: "Users", icon: Users },
    { path: "/logs", label: "System Logs", icon: FileText },
  ];

  const isActive = (item) => {
    const prefix = item.activePrefix || item.path;
    if (prefix === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(prefix);
  };

  return (
    <aside
      className={`hidden md:flex flex-col bg-white border-r border-warm-grey h-full transition-all duration-300 ${
        collapsed ? "w-16" : "w-64"
      }`}
      data-testid="sidebar"
    >
      {/* Logo & Toggle */}
      <div
        className={`p-4 flex items-center ${
          collapsed ? "justify-center" : "justify-between"
        }`}
      >
        {!collapsed && (
          <img
            src={LOGO_URL}
            alt="Knots"
            className="h-10 w-auto object-contain"
            data-testid="sidebar-logo"
          />
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="text-muted-foreground hover:text-foreground"
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <PanelLeft className="w-5 h-5" />
          ) : (
            <PanelLeftClose className="w-5 h-5" />
          )}
        </Button>
      </div>

      <Separator className="bg-warm-grey/50" />

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            title={collapsed ? item.label : undefined}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={`flex items-center gap-3 px-3 py-3 rounded-md font-body text-sm transition-all duration-200 group ${
              collapsed ? "justify-center" : ""
            } ${
              isActive(item)
                ? "bg-imperial/10 text-imperial font-medium"
                : "text-muted-foreground hover:bg-cream hover:text-foreground"
            }`}
          >
            <item.icon
              className={`w-5 h-5 flex-shrink-0 ${
                isActive(item) ? "text-imperial" : ""
              }`}
            />
            {!collapsed && (
              <>
                <span className="flex-1">{item.label}</span>
                {isActive(item) && (
                  <ChevronRight className="w-4 h-4 text-imperial" />
                )}
              </>
            )}
          </Link>
        ))}

        {isLeader() && (
          <>
            <div
              className={`pt-4 pb-2 ${collapsed ? "flex justify-center" : ""}`}
            >
              {collapsed ? (
                <Shield className="w-4 h-4 text-muted-foreground" />
              ) : (
                <span className="px-3 text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                  <Shield className="w-3 h-3" />
                  Admin
                </span>
              )}
            </div>
            {leaderMenuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                title={collapsed ? item.label : undefined}
                data-testid={`nav-${item.label
                  .toLowerCase()
                  .replace(" ", "-")}`}
                className={`flex items-center gap-3 px-3 py-3 rounded-md font-body text-sm transition-all duration-200 group ${
                  collapsed ? "justify-center" : ""
                } ${
                  isActive(item)
                    ? "bg-imperial/10 text-imperial font-medium"
                    : "text-muted-foreground hover:bg-cream hover:text-foreground"
                }`}
              >
                <item.icon
                  className={`w-5 h-5 flex-shrink-0 ${
                    isActive(item) ? "text-imperial" : ""
                  }`}
                />
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {isActive(item) && (
                      <ChevronRight className="w-4 h-4 text-imperial" />
                    )}
                  </>
                )}
              </Link>
            ))}
          </>
        )}
      </nav>

      <Separator className="bg-warm-grey/50" />

      {/* User Section */}
      <div className="p-2 space-y-1">
        <Link
          to="/profile"
          title={collapsed ? "Profile" : undefined}
          data-testid="nav-profile"
          className={`flex items-center gap-3 px-3 py-3 rounded-md font-body text-sm transition-all duration-200 ${
            collapsed ? "justify-center" : ""
          } ${
            isActive("/profile")
              ? "bg-imperial/10 text-imperial font-medium"
              : "text-muted-foreground hover:bg-cream hover:text-foreground"
          }`}
        >
          <Settings className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span>Profile</span>}
        </Link>

        {!collapsed && (
          <div className="px-3 py-2">
            <p className="font-body text-sm font-medium text-foreground truncate">
              {user?.full_name}
            </p>
            <p className="font-body text-xs text-muted-foreground truncate">
              {user?.email}
            </p>
            <span
              className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                user?.role === "leader"
                  ? "bg-gold/10 text-gold border border-gold/30"
                  : "bg-imperial/10 text-imperial border border-imperial/30"
              }`}
            >
              {user?.role === "leader" ? "Leader" : "Client"}
            </span>
          </div>
        )}

        <Button
          variant="ghost"
          onClick={handleLogout}
          title={collapsed ? "Sign Out" : undefined}
          className={`w-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 ${
            collapsed ? "justify-center px-3" : "justify-start"
          }`}
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="ml-3">Sign Out</span>}
        </Button>
      </div>
    </aside>
  );
};

export const Layout = () => {
  const {
    user,
    logout,
    isLeader,
    hasProjectAccess,
    hasInvitationAccess,
    getClientSingleProjectId,
    getClientSingleInvitationId,
  } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Build menu items based on access
  const menuItems = [];

  // Dashboard only for leaders
  if (isLeader()) {
    menuItems.push({
      path: "/dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
    });
  }

  // Projects if has access — clients go directly to their project
  if (hasProjectAccess()) {
    const clientProjectId = getClientSingleProjectId();
    menuItems.push({
      path: clientProjectId ? `/projects/${clientProjectId}` : "/projects",
      label: "Projects",
      icon: FolderHeart,
    });
  }

  // Invitations if has access — clients go directly to their invitation
  if (hasInvitationAccess()) {
    const clientInvitationId = getClientSingleInvitationId();
    menuItems.push({
      path: clientInvitationId
        ? `/invitations/${clientInvitationId}`
        : "/invitations",
      label: "Invitations",
      icon: Heart,
    });
  }

  const leaderMenuItems = [
    { path: "/categories", label: "Categories", icon: Tags },
    { path: "/users", label: "Users", icon: Users },
    { path: "/logs", label: "System Logs", icon: FileText },
  ];

  return (
    <SidebarContext.Provider
      value={{ collapsed: sidebarCollapsed, setCollapsed: setSidebarCollapsed }}
    >
      <div
        className="flex h-screen overflow-hidden bg-cream"
        data-testid="app-layout"
      >
        {/* Desktop Sidebar */}
        <DesktopSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        {/* Main Content */}
        <main className="flex-1 flex flex-col overflow-hidden relative">
          {/* Mobile Header */}
          <header className="md:hidden flex items-center justify-between p-4 bg-white border-b border-warm-grey">
            <img src={LOGO_URL} alt="Knots" className="h-8 w-auto" />
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="mobile-menu-button"
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0 bg-white">
                <div className="p-6">
                  <img src={LOGO_URL} alt="Knots" className="h-10 w-auto" />
                </div>
                <Separator className="bg-warm-grey/50" />
                <nav className="p-4 space-y-1">
                  {menuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-md font-body text-sm text-muted-foreground hover:bg-cream hover:text-foreground transition-all"
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </Link>
                  ))}
                  {isLeader() && (
                    <>
                      <div className="pt-4 pb-2">
                        <span className="px-4 text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                          <Shield className="w-3 h-3" />
                          Admin
                        </span>
                      </div>
                      {leaderMenuItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-md font-body text-sm text-muted-foreground hover:bg-cream hover:text-foreground transition-all"
                        >
                          <item.icon className="w-5 h-5" />
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </>
                  )}
                </nav>
                <Separator className="bg-warm-grey/50" />
                <div className="p-4 space-y-3">
                  <Link
                    to="/profile"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-md font-body text-sm text-muted-foreground hover:bg-cream hover:text-foreground transition-all"
                  >
                    <Settings className="w-5 h-5" />
                    <span>Profile</span>
                  </Link>
                  <div className="px-4 py-2">
                    <p className="font-body text-sm font-medium">
                      {user?.full_name}
                    </p>
                    <p className="font-body text-xs text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={handleLogout}
                    className="w-full justify-start text-muted-foreground hover:text-destructive"
                  >
                    <LogOut className="w-5 h-5 mr-3" />
                    Sign Out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </header>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-4 md:p-8 scroll-smooth">
            <Outlet />
          </div>
        </main>
      </div>
    </SidebarContext.Provider>
  );
};

export default Layout;
